/**
 * Infinite Scroll for Product Grid
 * Automatically loads next page when user scrolls near bottom
 */

class InfiniteScroll {
  constructor() {
    this.productGrid = document.querySelector('#product-grid');
    this.isLoading = false;
    this.observer = null;
    this.init();
  }

  init() {
    if (!this.productGrid) return;

    // Use IntersectionObserver for better performance and accuracy
    this.setupObserver();
  }

  setupObserver() {
    // Disconnect existing observer if any
    if (this.observer) {
      this.observer.disconnect();
    }

    const trigger = document.querySelector('.infinite-scroll-trigger');
    if (!trigger) return;

    // Create observer with 800px margin for smooth experience
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Load when trigger becomes visible
          if (entry.isIntersecting && !this.isLoading) {
            this.loadNextPage();
          }
        });
      },
      {
        // Start loading when trigger is 800px away from viewport
        rootMargin: '800px',
        threshold: 0
      }
    );

    // Start observing the trigger
    this.observer.observe(trigger);
  }

  async loadNextPage() {
    const trigger = document.querySelector('.infinite-scroll-trigger');
    if (!trigger) return;

    const nextUrl = trigger.dataset.nextUrl;
    if (!nextUrl || nextUrl === 'null' || nextUrl === 'undefined') return;

    if (this.isLoading) return;
    this.isLoading = true;

    // Show loading spinner
    this.showLoader();

    try {
      const response = await fetch(nextUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newProductGrid = doc.querySelector('#product-grid');

      if (newProductGrid && newProductGrid.children.length > 0) {
        // Use DocumentFragment for better performance
        const fragment = document.createDocumentFragment();

        Array.from(newProductGrid.children).forEach((child) => {
          const clonedChild = child.cloneNode(true);

          // Force lazy loading on all new images
          const images = clonedChild.querySelectorAll('img');
          images.forEach(img => {
            img.loading = 'lazy';
          });

          fragment.appendChild(clonedChild);

          // Re-execute inline scripts for image navigation
          const scripts = clonedChild.querySelectorAll('script');
          scripts.forEach(script => {
            try {
              eval(script.textContent);
            } catch (e) {
              // Silent fail for script errors
            }
          });
        });

        // Append all at once for better performance
        this.productGrid.appendChild(fragment);
      }

      // Remove old trigger and stop observing it
      if (this.observer) {
        this.observer.disconnect();
      }
      trigger.remove();

      // Add new trigger if there's a next page
      const newTrigger = doc.querySelector('.infinite-scroll-trigger');
      if (newTrigger && newTrigger.dataset.nextUrl) {
        this.productGrid.parentElement.appendChild(newTrigger);
        // Set up observer for the new trigger
        this.setupObserver();
      }

    } catch (error) {
      // Silent fail - infinite scroll is not critical
    } finally {
      this.isLoading = false;
      this.hideLoader();
    }
  }

  showLoader() {
    // Create loader if it doesn't exist
    if (!this.loader) {
      this.loader = document.createElement('div');
      this.loader.className = 'infinite-scroll-loader';
      this.loader.innerHTML = `
        <div class="infinite-scroll-loader__spinner">
          <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
            <circle cx="20" cy="20" r="18" fill="none" stroke="currentColor" stroke-width="3" stroke-dasharray="90 150" stroke-linecap="round">
              <animateTransform
                attributeName="transform"
                type="rotate"
                from="0 20 20"
                to="360 20 20"
                dur="1s"
                repeatCount="indefinite"/>
            </circle>
          </svg>
          <p>Chargement des produits...</p>
        </div>
      `;

      // Add loader styles
      const style = document.createElement('style');
      style.textContent = `
        .infinite-scroll-loader {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 40px 20px;
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .infinite-scroll-loader.visible {
          opacity: 1;
        }
        .infinite-scroll-loader__spinner {
          text-align: center;
          color: rgba(var(--color-foreground), 0.75);
        }
        .infinite-scroll-loader__spinner svg {
          display: block;
          margin: 0 auto 12px;
        }
        .infinite-scroll-loader__spinner p {
          margin: 0;
          font-size: 14px;
        }
      `;
      document.head.appendChild(style);
    }

    // Insert loader before the trigger
    const trigger = document.querySelector('.infinite-scroll-trigger');
    if (trigger && !this.loader.parentElement) {
      trigger.parentElement.insertBefore(this.loader, trigger);
    }

    // Show loader with animation
    requestAnimationFrame(() => {
      this.loader.classList.add('visible');
    });
  }

  hideLoader() {
    if (this.loader) {
      this.loader.classList.remove('visible');
      // Remove from DOM after animation
      setTimeout(() => {
        if (this.loader && this.loader.parentElement) {
          this.loader.remove();
        }
      }, 300);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new InfiniteScroll();
  });
} else {
  new InfiniteScroll();
}
